import { useState, useEffect } from 'react';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { CurrencyInput } from '@/components/ui/currency-input';
import { Percent, DollarSign } from 'lucide-react';
import { cn } from '@/lib/utils';
import { roundMoney } from '@/utils/orderCalculator';

interface DiscountSelectorProps {
  subtotal: number;
  discountPercentage: number;
  discountValue: number;
  onDiscountChange: (percentage: number, value: number) => void;
  disabled?: boolean;
  compact?: boolean;
  /** Quantidade de unidades - necessário para calcular o preço unitário e limitar desconto */
  quantity?: number;
  /** Tipo de desconto inicial (para inicializar o modo correto ao editar) */
  initialDiscountType?: 'percentage' | 'fixed' | null;
  /** Callback para informar o tipo de desconto selecionado pelo usuário */
  onDiscountTypeChange?: (type: 'percentage' | 'fixed') => void;
}

export function DiscountSelector({
  subtotal,
  discountPercentage,
  discountValue,
  onDiscountChange,
  disabled,
  compact = false,
  quantity = 1, // Default para evitar divisão por zero
  initialDiscountType,
  onDiscountTypeChange,
}: DiscountSelectorProps) {
  const [mode, setMode] = useState<'percentage' | 'value'>('percentage');
  const [inputValue, setInputValue] = useState<number>(0);
  const [isUserEditing, setIsUserEditing] = useState(false);
  const [wasCleared, setWasCleared] = useState(false);
  const [hasInitialized, setHasInitialized] = useState(false);

  // Efeito de INICIALIZAÇÃO: usar discount_type para definir modo correto
  useEffect(() => {
    if (hasInitialized) return;
    
    // CRITICAL: Se temos um discount_type definido, usar ele para definir o modo
    if (initialDiscountType === 'fixed') {
      setMode('value');
      setInputValue(discountValue);
      setHasInitialized(true);
      return;
    } else if (initialDiscountType === 'percentage') {
      setMode('percentage');
      setInputValue(discountPercentage);
      setHasInitialized(true);
      return;
    }
    
    // LEGADO: Se não há discount_type, usar lógica antiga de inferência
    if (discountValue > 0 || discountPercentage > 0) {
      if (discountPercentage > 0) {
        setMode('percentage');
        setInputValue(discountPercentage);
      } else if (discountValue > 0) {
        setMode('value');
        setInputValue(discountValue);
      }
      setHasInitialized(true);
    }
  }, [initialDiscountType, discountValue, discountPercentage, hasInitialized]);

  // Efeito de ATUALIZAÇÃO: sincronizar inputValue com props após inicialização
  useEffect(() => {
    // Se o usuário está editando, nunca interferir
    if (isUserEditing) {
      return;
    }
    
    // Se foi zerado intencionalmente, manter zerado até props confirmarem
    if (wasCleared) {
      if (discountValue === 0 && discountPercentage === 0) {
        setWasCleared(false);
      }
      return;
    }
    
    // Reset externo (ambos zerados)
    if (discountValue === 0 && discountPercentage === 0) {
      setInputValue(0);
      // NÃO mudar o modo - preservar seleção do usuário
      return;
    }
    
    // Apenas atualizar valor baseado no modo ATUAL (sem trocar de modo)
    if (hasInitialized) {
      if (mode === 'percentage' && discountPercentage > 0) {
        setInputValue(discountPercentage);
      } else if (mode === 'value' && discountValue > 0) {
        setInputValue(discountValue);
      }
    }
  }, [discountValue, discountPercentage, mode, isUserEditing, wasCleared, hasInitialized]);

  const handleModeChange = (newMode: 'percentage' | 'value') => {
    setIsUserEditing(false);
    setWasCleared(false); // Reset ao mudar modo
    setMode(newMode);
    setInputValue(0);
    onDiscountChange(0, 0);
    // Notificar o tipo de desconto selecionado
    onDiscountTypeChange?.(newMode === 'percentage' ? 'percentage' : 'fixed');
    // CRITICAL: Travar o modo escolhido pelo usuário para evitar re-inferência
    setHasInitialized(true);
  };

  const handleInputChange = (value: number) => {
    setIsUserEditing(true);
    // CRITICAL: Travar o modo ao começar digitação para evitar re-inferência
    setHasInitialized(true);
    setInputValue(value);
    
    // Marcar se o usuário zerou intencionalmente
    if (value === 0) {
      setWasCleared(true);
    }

    if (mode === 'percentage') {
      const percentage = Math.min(100, Math.max(0, value));
      // Calcular desconto por unidade a partir da percentagem
      const unitPrice = quantity > 0 ? subtotal / quantity : subtotal;
      const calculatedValuePerUnit = roundMoney((unitPrice * percentage) / 100);
      onDiscountChange(percentage, calculatedValuePerUnit);
      // REMOVIDO: onDiscountTypeChange - só deve ser chamado ao TROCAR de modo
    } else {
      // Desconto em R$ é POR UNIDADE (semântica do sistema)
      // Calcular preço unitário para validar limite máximo
      const unitPrice = quantity > 0 ? subtotal / quantity : subtotal;
      const valuePerUnit = Math.min(unitPrice, Math.max(0, value));
      const calculatedPercentage = unitPrice > 0 ? roundMoney((valuePerUnit / unitPrice) * 100) : 0;
      // Retornar o valor POR UNIDADE
      onDiscountChange(calculatedPercentage, valuePerUnit);
      // REMOVIDO: onDiscountTypeChange - só deve ser chamado ao TROCAR de modo
    }
    
    // Liberar flag após o ciclo de atualização completar
    setTimeout(() => setIsUserEditing(false), 100);
  };

  if (compact) {
    return (
      <div className="flex items-center gap-2">
        <div className="flex rounded-md border border-input overflow-hidden">
          <Button
            type="button"
            size="sm"
            variant={mode === 'percentage' ? 'default' : 'ghost'}
            className={cn(
              'rounded-none h-8 w-8 p-0',
              mode === 'percentage' ? 'bg-primary' : 'hover:bg-muted'
            )}
            onClick={() => handleModeChange('percentage')}
            disabled={disabled}
          >
            <Percent className="h-3 w-3" />
          </Button>
          <Button
            type="button"
            size="sm"
            variant={mode === 'value' ? 'default' : 'ghost'}
            className={cn(
              'rounded-none h-8 w-8 p-0',
              mode === 'value' ? 'bg-primary' : 'hover:bg-muted'
            )}
            onClick={() => handleModeChange('value')}
            disabled={disabled}
          >
            <DollarSign className="h-3 w-3" />
          </Button>
        </div>
        {mode === 'percentage' ? (
          <Input
            type="number"
            value={inputValue || ''}
            onChange={(e) => {
              const raw = e.currentTarget?.value ?? e.target?.value ?? '';
              handleInputChange(parseFloat(raw) || 0);
            }}
            placeholder="0"
            disabled={disabled}
            className="h-8 w-20"
            min="0"
            max="100"
            step="0.01"
            onFocus={(e) => {
              const el = e.currentTarget;
              setTimeout(() => {
                if (el?.isConnected) el.scrollIntoView({ behavior: 'smooth', block: 'center' });
              }, 300);
            }}
          />
        ) : (
          <CurrencyInput
            value={inputValue || 0}
            onChange={handleInputChange}
            placeholder="R$ 0,00"
            disabled={disabled}
            className="h-8 w-28"
          />
        )}
      </div>
    );
  }

  return (
    <div className="flex items-center gap-3">
      <span className="text-sm font-medium">Desconto:</span>
      <div className="flex items-center gap-2">
        <div className="flex rounded-md border border-input overflow-hidden">
          <Button
            type="button"
            size="sm"
            variant={mode === 'percentage' ? 'default' : 'ghost'}
            className={cn(
              'rounded-none px-3',
              mode === 'percentage' ? 'bg-primary' : 'hover:bg-muted'
            )}
            onClick={() => handleModeChange('percentage')}
            disabled={disabled}
          >
            <Percent className="h-4 w-4 mr-1" />%
          </Button>
          <Button
            type="button"
            size="sm"
            variant={mode === 'value' ? 'default' : 'ghost'}
            className={cn(
              'rounded-none px-3',
              mode === 'value' ? 'bg-primary' : 'hover:bg-muted'
            )}
            onClick={() => handleModeChange('value')}
            disabled={disabled}
          >
            <DollarSign className="h-4 w-4 mr-1" />
            R$
          </Button>
        </div>
        {mode === 'percentage' ? (
          <Input
            type="number"
            value={inputValue || ''}
            onChange={(e) => {
              const raw = e.currentTarget?.value ?? e.target?.value ?? '';
              handleInputChange(parseFloat(raw) || 0);
            }}
            placeholder="0"
            disabled={disabled}
            className="w-24"
            min="0"
            max="100"
            step="0.01"
            onFocus={(e) => {
              const el = e.currentTarget;
              setTimeout(() => {
                if (el?.isConnected) el.scrollIntoView({ behavior: 'smooth', block: 'center' });
              }, 300);
            }}
          />
        ) : (
          <CurrencyInput
            value={inputValue || 0}
            onChange={handleInputChange}
            placeholder="R$ 0,00"
            disabled={disabled}
            className="w-32"
          />
        )}
      </div>
    </div>
  );
}
